import { useCallback, useEffect, useRef } from "react";
import { STARTER_NODE_UI_ID } from "../ruleBasedFlow.constants";
import * as ruleBasedFlowService from "../ruleBasedFlow.service";

/**
 * Custom hook to persist node positions using debounced updates
 * @param {string} context - Context: "workflow" | "task"
 * @param {string} entityId - Workflow ID or Task ID
 * @param {boolean} isReadOnly - Whether the flow is read-only
 * @param {Array} nodes - Current nodes array
 * @returns {Function} Handler function for onNodesChange
 */
export const useNodePositionPersistence = (context, entityId, isReadOnly, nodes) => {
  const pendingUpdatesRef = useRef(new Map()); // Map<nodeId, {x, y}>
  const debounceTimerRef = useRef(null);
  const lastSavedPositionsRef = useRef(new Map()); // Track last saved positions to avoid unnecessary updates

  /**
   * Saves pending position updates to backend
   * Uses task-specific endpoint in task context
   */
  const savePendingPositions = useCallback(async () => {
    if (pendingUpdatesRef.current.size === 0) return;

    const updates = Array.from(pendingUpdatesRef.current.entries());
    pendingUpdatesRef.current.clear();

    // Save each position update
    const updatePromises = updates.map(async ([nodeId, position]) => {
      try {
        if (context === "task") {
          await ruleBasedFlowService.updateTaskNode({
            nodeId,
            taskId: parseInt(entityId),
            position,
          });
        } else {
          await ruleBasedFlowService.updateNode({
            nodeId,
            workflowId: parseInt(entityId),
            position,
          });
        }
        // Update last saved position on success
        lastSavedPositionsRef.current.set(nodeId, position);
      } catch (error) {
        // Silent failure - no error notification
        console.error(`Failed to save position for node ${nodeId}:`, error);
      }
    });

    await Promise.all(updatePromises);
  }, [entityId, context]);

  /**
   * Debounced save function
   */
  const debouncedSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      savePendingPositions();
    }, 500); // 500ms debounce
  }, [savePendingPositions]);

  /**
   * Handles node changes and tracks position updates
   */
  const handleNodesChange = useCallback(
    (changes) => {
      // Skip if read-only or no entityId
      if (isReadOnly || !entityId) {
        return;
      }

      changes.forEach((change) => {
        // Only process position changes
        if (change.type === "position" && change.position) {
          const nodeId = change.id;

          // Skip starter node (UI-only)
          if (nodeId === STARTER_NODE_UI_ID) {
            return;
          }

          const newPosition = change.position;
          const lastSaved = lastSavedPositionsRef.current.get(nodeId);

          // Only update if position actually changed
          if (!lastSaved || lastSaved.x !== newPosition.x || lastSaved.y !== newPosition.y) {
            pendingUpdatesRef.current.set(nodeId, { ...newPosition });
            debouncedSave();
          }
        }
      });
    },
    [isReadOnly, entityId, debouncedSave]
  );

  /**
   * Initialize last saved positions from current nodes
   */
  useEffect(() => {
    if (nodes && nodes.length > 0) {
      nodes.forEach((node) => {
        if (node.id !== STARTER_NODE_UI_ID && node.position) {
          lastSavedPositionsRef.current.set(node.id, node.position);
        }
      });
    }
  }, [nodes]);

  /**
   * Cleanup: Save any pending positions on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Save any remaining pending positions
      if (pendingUpdatesRef.current.size > 0) {
        savePendingPositions();
      }
    };
  }, [savePendingPositions]);

  return handleNodesChange;
};

